import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../store/authStore";
import SEO from "../components/SEO";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [searchParams] = useSearchParams();
  const emailVerified = searchParams.get("verified") === "true";
  const { login, loading, error, clearError, user, fetchMe } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchMe(); }, [fetchMe]);
  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try { await login(email, password); navigate("/dashboard"); } catch { /* error in store */ }
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <SEO title="Sign In" description="Sign in to your Madrich account to access your personalized college admissions roadmap, essay coach, and more." canonical="/login" />
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 border border-white/10 rounded-full" />
          <div className="absolute bottom-20 right-10 w-96 h-96 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white/5 rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <Link to="/" className="text-3xl font-bold mb-8">Madrich</Link>
          <h2 className="text-4xl font-bold leading-tight mb-4">
            Your AI guide through<br />college admissions
          </h2>
          <p className="text-blue-100 text-lg leading-relaxed max-w-md">
            Clear direction for students and parents, every step of the way.
          </p>
          <div className="flex gap-8 mt-12">
            <div>
              <div className="text-3xl font-bold">1,900+</div>
              <div className="text-blue-100 text-sm">Colleges</div>
            </div>
            <div>
              <div className="text-3xl font-bold">24/7</div>
              <div className="text-blue-100 text-sm">AI guidance</div>
            </div>
            <div>
              <div className="text-3xl font-bold">$299</div>
              <div className="text-blue-100 text-sm">Per year</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-8 block">
            Madrich
          </Link>

          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome back</h1>
          <p className="text-slate-500 mb-8">Sign in to continue your admissions journey</p>

          {emailVerified && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 text-sm">
              Email verified! Sign in to continue.
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-center justify-between">
              {error}
              <button onClick={clearError} className="text-red-400 hover:text-red-600 ml-2">&times;</button>
            </div>
          )}

          <a
            href="/api/auth/google"
            className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 py-3 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all font-medium text-slate-700 mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </a>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-slate-500 uppercase tracking-wide">or sign in with email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-slate-50 focus:bg-white"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-slate-50 focus:bg-white"
                placeholder="Min 8 characters"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 transition-all duration-300"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-slate-500 space-y-2">
            <p>
              <Link to="/forgot-password" className="text-slate-500 hover:text-blue-600 hover:underline">Forgot your password?</Link>
            </p>
            <p>
              Don&apos;t have an account?{" "}
              <Link to="/register" className="text-blue-600 font-semibold hover:underline">Create one free</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
